import { Injectable } from '@angular/core';

import { ContractType, PendingContractResponse } from '@app-model/api';
import { ContractFilterGroupType, ContractFilterType } from '@app-model/remit-filter.model';

@Injectable({
  providedIn: 'root',
})
export class RemitService {
  static fetchContractGroups(contracts: ContractType[]): ContractFilterType[] {
    return contracts.reduce((prevVal, elem) => {
      const index = prevVal.some((item) => item.name === elem.contractGroup1);
      if (!index) {
        prevVal.push(new ContractFilterType(elem.contractGroup1));
      }

      return prevVal;
    }, []);
  }

  static fetchRemittedContractGroups(contracts: ContractType[]): ContractFilterGroupType[] {
    return contracts.reduce((prevVal, elem) => {
      const index = prevVal.some((item) => item.name === elem.contractGroup1);
      if (!index) {
        const filterGroup = this.fetchRemittedContractGroup2(elem.contractGroup1, contracts);
        prevVal.push(new ContractFilterGroupType(elem.contractGroup1, false, filterGroup));
      }

      return prevVal;
    }, []);
  }

  static fetchRemittedContractGroup2(contractGroup1: string, contracts: ContractType[]): ContractFilterType[] {
    return contracts.reduce((prevVal, elem) => {
      if (elem.contractGroup1 === contractGroup1) {
        const index = prevVal.some((item) => item.name === elem.contractGroup2);
        if (!index) {
          prevVal.push(new ContractFilterType(elem.contractGroup2));
        }
      }

      return prevVal;
    }, []);
  }

  ////////// STATIC FILTER PRODUCTS ////////////////////////////////////////////////////////

  static filterPendingGrid(pendingContractFilter: ContractFilterType[], contracts: ContractType[]): ContractType[] {
    // get changed product filters
    let allContracts = true;

    // Filter products by product name
    const filteredPendingContracts = contracts.filter((data) => {
      const index = pendingContractFilter.findIndex((x) => x.name === data.contractGroup1);
      if (index > -1) {
        // uncheck allProduct button
        if (pendingContractFilter[index].selected) {
          allContracts = false;
        }

        return pendingContractFilter[index].selected;
      } else {
        return true;
      }
    });

    if (allContracts) {
      return contracts;
    }

    return filteredPendingContracts;
  }

  static filterRemittedGrid(
    remittedContractFilter: ContractFilterGroupType[],
    contracts: ContractType[]
  ): ContractType[] {
    // get filters
    let allContractsForGroupFlag = true;

    const allContractsForGroup = this.filterByGroupType(remittedContractFilter, contracts);

    // Filter products by product name
    const filteredContracts = contracts.filter((data) => {
      const index = remittedContractFilter.findIndex((x) => x.name === data.contractGroup1);
      if (index > -1) {
        // uncheck allProduct button
        if (remittedContractFilter[index].selected) {
          const filterFlag = this.filterByContractGroup2(remittedContractFilter[index].batch, data);
          if (filterFlag) {
            allContractsForGroupFlag = false;
          }

          return filterFlag;
        } else {
          return false;
        }
      } else {
        return true;
      }
    });

    if (allContractsForGroupFlag) {
      return allContractsForGroup;
    }

    return filteredContracts;
  }

  static filterByGroupType(
    remittedContractFilter: ContractFilterGroupType[],
    contracts: ContractType[]
  ): ContractType[] {
    // get filters
    let allContracts = true;

    // Filter products by product name
    const filteredPendingContracts = contracts.filter((data) => {
      const index = remittedContractFilter.findIndex((x) => x.name === data.contractGroup1);
      if (index > -1) {
        // uncheck allProduct button
        if (remittedContractFilter[index].selected) {
          allContracts = false;
        }

        return remittedContractFilter[index].selected;
      } else {
        return true;
      }
    });

    if (allContracts) {
      return contracts;
    }

    return filteredPendingContracts;
  }

  static filterByContractGroup2(contractGroup2: ContractFilterType[], contract: ContractType): boolean {
    let filterFlag = false;
    contractGroup2.forEach((item) => {
      if (item.name === contract.contractGroup2) {
        filterFlag = item.selected;

        return;
      }
    });

    return filterFlag;
  }

  // /////////////////////////////////////////////////////////////////////////

  ///////////////////  VALIDATION FUNCTIONS  /////////////////////////////////

  static declineAndUpdateContracts(
    pendingGridContracts: ContractType[],
    contractToDecline: ContractType
  ): ContractType[] {
    if (contractToDecline.dupError) {
      // check for more than 1 duplicate. if more than 1, do not change change duplicate flag.
      let dupNums = 0;
      contractToDecline.dupArray.forEach((dupContractNumber) => {
        const otherDupContract = pendingGridContracts.findIndex(
          (contract) => contract.econContractNumber === dupContractNumber
        );
        if (otherDupContract > -1) {
          if (pendingGridContracts[otherDupContract].dupError) {
            dupNums += 1;
          }
        }
      });

      contractToDecline.dupArray.forEach((dupContractNumber) => {
        const otherDupContract = pendingGridContracts.findIndex(
          (contract) => contract.econContractNumber === dupContractNumber
        );
        if (otherDupContract > -1) {
          if (pendingGridContracts[otherDupContract].dupError && dupNums <= 1) {
            pendingGridContracts[otherDupContract].dupError = false;
          }
        }
      });
    }

    contractToDecline.decline = true;
    contractToDecline.dupError = false;

    const foundContractIndx = pendingGridContracts.findIndex(
      (contract) => contract.econContractNumber === contractToDecline.econContractNumber
    );

    pendingGridContracts[foundContractIndx] = contractToDecline;

    return pendingGridContracts;
  }

  static reinstateAndUpdateContracts(
    pendingGridContracts: ContractType[],
    contractToReinstate: ContractType
  ): ContractType[] {
    // check for duplicate array to see if have not been remitted/declined.
    // if any still in this list and was a original dup with no decline/remit, then change the
    // contract to duplicate.
    if (contractToReinstate.origDup) {
      let actionTakenOnAllDuplicates = true;

      contractToReinstate.dupArray.forEach((dupContractNumber) => {
        const otherDupContract = pendingGridContracts.findIndex(
          (contract) => contract.econContractNumber === dupContractNumber
        );
        if (otherDupContract > -1) {
          const currentContract = pendingGridContracts[otherDupContract];
          // if action hasnt been taken on ANY contracts whether its declined or remitted
          if (currentContract.origDup && currentContract.addToRemit) {
            pendingGridContracts[otherDupContract].addToRemit = false;
          }
        }
      });

      contractToReinstate.dupArray.forEach((dupContractNumber) => {
        const otherDupContract = pendingGridContracts.findIndex(
          (contract) => contract.econContractNumber === dupContractNumber
        );
        if (otherDupContract > -1) {
          const currentContract = pendingGridContracts[otherDupContract];
          // if action hasnt been taken on ANY contracts whether its declined or remitted
          if (currentContract.origDup && !currentContract.decline && !currentContract.addToRemit) {
            pendingGridContracts[otherDupContract].dupError = true;
            actionTakenOnAllDuplicates = false;
          }
        }
      });

      // if action has been taken on all duplicates then do not set duplicate to true
      contractToReinstate.dupError = !actionTakenOnAllDuplicates;
    } else {
      contractToReinstate.dupError = false;
    }
    contractToReinstate.decline = false;

    const contractIndex = pendingGridContracts.findIndex(
      (contract) => contract.econContractNumber === contractToReinstate.econContractNumber
    );

    pendingGridContracts[contractIndex] = { ...contractToReinstate };

    return pendingGridContracts;
  }

  // /////////////////////////////////////////////////////////////////////////

  ///////////////////  RESET CONTRACTS  /////////////////////////////////

  static resetContracts(response: PendingContractResponse): PendingContractResponse {
    response.pendingContracts = response.pendingContracts.map((contract) => {
      contract.addToRemit = false;
      contract.decline = false;

      return contract;
    });

    return response;
  }
}
